import { Component, type ReactNode } from 'react'
import { useRouteError } from 'react-router-dom'
import { TriangleAlert } from 'lucide-react'

import { ErrorState, buttonClasses } from '../ui'
import { reportarErrorDelNavegador } from '../lib/erroresDelNavegador'

/**
 * Lo que se ve cuando la aplicación se rompe de verdad.
 *
 * ANTES DE ESTO: nada. Un fallo de render dejaba la pantalla EN BLANCO. El
 * usuario no sabe si cargó mal, si se cayó su conexión o si el sitio ya no
 * existe, así que hace lo único que puede: cerrar. Y aquí eso suele pasar a
 * mitad de una subida de varios gigas.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Dos capas, porque tapan cosas distintas
 * ─────────────────────────────────────────────────────────────────────────────
 * `ErrorDeRuta` es el `errorElement` del router: coge lo que revienta DENTRO de
 * una ruta, que es casi todo. React Router lo atrapa antes de que suba, así que
 * una frontera de clase por encima no lo vería nunca.
 *
 * `FronteraDeError` envuelve al router entero y coge lo de fuera: los
 * proveedores, el propio `Root`, el primer render. Sin ella, un fallo ahí sigue
 * dando pantalla blanca — que es justo el caso más grave.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Las dos informan, y tienen que hacerlo a mano
 * ─────────────────────────────────────────────────────────────────────────────
 * Un error atrapado NO llega a `window.onerror`: para eso sirve atraparlo. Sin
 * estas llamadas, montar las fronteras habría hecho que los fallos más graves
 * —los que rompen la pantalla entera— fueran justo los únicos que dejan de
 * contarse. Se gana una pantalla y se pierde la señal.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Qué NO hace la pantalla
 * ─────────────────────────────────────────────────────────────────────────────
 * No enseña el mensaje del error. No le dice nada a quien lo lee —«Cannot read
 * properties of undefined»— y sí puede llevar dentro una ruta, un identificador
 * o un trozo de consulta. Va al registro, que es donde se mira.
 *
 * Y no ofrece «reintentar» a secas: si el fallo es de render, volver a montar lo
 * mismo vuelve a fallar y deja al usuario dando toques. Recarga la página, que
 * es lo que de verdad tiene ocasión de arreglarlo.
 */
function Pantalla() {
  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <ErrorState
        icon={TriangleAlert}
        title="Se rompió algo por nuestra parte"
        description="No es cosa tuya ni de tu conexión. Ya nos hemos enterado; vuelve a cargar la página y sigue donde estabas."
        action={
          <button
            type="button"
            className={buttonClasses('primary', 'md')}
            onClick={() => location.reload()}
          >
            Volver a cargar
          </button>
        }
      />
    </div>
  )
}

/** El `errorElement` del router. Coge lo que revienta dentro de una ruta. */
export function ErrorDeRuta() {
  const error = useRouteError()
  reportarErrorDelNavegador('frontera-de-ruta', error)

  return <Pantalla />
}

/**
 * La frontera de fuera del router. En clase porque `componentDidCatch` no tiene
 * equivalente en función: es lo único que React no ha movido a hooks.
 */
export class FronteraDeError extends Component<{ children: ReactNode }, { roto: boolean }> {
  override state = { roto: false }

  static getDerivedStateFromError() {
    return { roto: true }
  }

  override componentDidCatch(error: unknown) {
    reportarErrorDelNavegador('frontera-raiz', error)
  }

  override render() {
    return this.state.roto ? <Pantalla /> : this.props.children
  }
}
